/**
 * Per-team evidence submission health. One pure function, no I/O, no Date.now() read
 * internally (the caller passes `now`) - this is what makes it exhaustively unit-testable
 * and what the spec means by "never stored as a manual field": nothing here is a stored
 * status column anywhere, it's recomputed from submission data on every read.
 *
 * Four independent checks, each returns its own status + a plain-language reason. The
 * team's overall status is the worst of its checks. "Worst" ordering, worst to best:
 *   fail > pending > warning > pass
 * Fail always wins (something is definitively wrong). Pending beats warning deliberately -
 * "nothing has failed, but we don't have enough information yet" (e.g. not submitted, due
 * date not yet reached) reads as a weaker claim than "submitted, but something needs
 * attention", so Pending should not be hidden behind a Warning from an unrelated check.
 */

export type CheckStatus = 'pass' | 'warning' | 'fail' | 'pending';
export type TeamHealthStatus = 'Healthy' | 'Needs attention' | 'Failing' | 'Pending';

export interface CheckResult {
  id: 'timing' | 'capturedInPeriod' | 'requirementsComplete' | 'attachmentProvided';
  label: string;
  status: CheckStatus;
  reason: string;
}

export interface TeamHealthResult {
  status: TeamHealthStatus;
  checks: CheckResult[];
}

export interface RequirementItem {
  id: string;
  done: boolean;
}

export interface AttachmentItem {
  id: string;
  /** ISO 8601 date or datetime - when the evidence itself was captured (file last-modified
   * for an upload, manual or current time for a link), never the upload/submit time. */
  capturedAt: string;
}

export interface TeamHealthInput {
  /** ISO date (YYYY-MM-DD). The cycle's due date - unaffected by this change, still the
   * evidence item's own rolling next_due_date. */
  dueDate: string;
  /** Whole days. 0 is valid (no grace at all). */
  gracePeriodDays: number;
  /** ISO dates (YYYY-MM-DD), the round's stamped review period. */
  reviewPeriodStart: string;
  reviewPeriodEnd: string;
  /** ISO datetime, or null if this team has not submitted this round yet. */
  submittedAt: string | null;
  requirements: RequirementItem[];
  attachments: AttachmentItem[];
  /** Injected, not read internally - the one thing that makes "not yet submitted, is it
   * before/inside/past the due date + grace" testable without mocking the clock. Always
   * pass the server's time per the no-trust-the-client-clock rule - this function just
   * computes with whatever `now` it's given. */
  now: Date;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function parseDate(iso: string): Date {
  // Date-only ISO strings (YYYY-MM-DD) parse as UTC midnight in every engine; datetime
  // strings keep their own offset. Both compare correctly against each other as long as
  // nothing here does its own timezone-local arithmetic, which it doesn't.
  return new Date(iso);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

function formatShortDate(iso: string): string {
  const d = parseDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

function checkTiming(input: TeamHealthInput): CheckResult {
  // `due` is midnight-start of the due date, used only for display/day-difference math.
  // Boundary comparisons use EXCLUSIVE end-of-day cutoffs instead ("< start of the next
  // day"), since a due date is a calendar day, not an instant - a submission at 10am on the
  // due date is still on time, not late, and a plain `submittedAt.getTime() <= due.getTime()`
  // (due parsed as that day's midnight) would wrongly fail it.
  const due = parseDate(input.dueDate);
  const dueCutoff = addDays(due, 1); // start of the day AFTER the due date
  const graceCutoff = addDays(due, 1 + input.gracePeriodDays); // start of the day after the last grace day
  const label = 'Submission timing';

  if (input.submittedAt) {
    const submitted = parseDate(input.submittedAt);
    if (submitted.getTime() < dueCutoff.getTime()) {
      const daysEarly = daysBetween(submitted, due);
      return {
        id: 'timing', label, status: 'pass',
        reason: daysEarly > 0
          ? `Submitted ${daysEarly} day${daysEarly === 1 ? '' : 's'} before the due date (${formatShortDate(input.dueDate)}).`
          : `Submitted on the due date (${formatShortDate(input.dueDate)}).`,
      };
    }
    if (submitted.getTime() < graceCutoff.getTime()) {
      const daysLate = daysBetween(due, submitted);
      return {
        id: 'timing', label, status: 'warning',
        reason: `Submitted ${daysLate} day${daysLate === 1 ? '' : 's'} after the due date (${formatShortDate(input.dueDate)}), within the ${input.gracePeriodDays}-day grace window.`,
      };
    }
    const daysLate = daysBetween(due, submitted);
    return {
      id: 'timing', label, status: 'fail',
      reason: `Submitted ${daysLate} days after the due date (${formatShortDate(input.dueDate)}), past the ${input.gracePeriodDays}-day grace window.`,
    };
  }

  // Not submitted yet - same three bands, relative to `now` instead of `submittedAt`.
  if (input.now.getTime() < dueCutoff.getTime()) {
    return {
      id: 'timing', label, status: 'pending',
      reason: `Not yet submitted. Due ${formatShortDate(input.dueDate)}.`,
    };
  }
  if (input.now.getTime() < graceCutoff.getTime()) {
    const daysOverdue = daysBetween(due, input.now);
    return {
      id: 'timing', label, status: 'warning',
      reason: `Not yet submitted. ${daysOverdue} day${daysOverdue === 1 ? '' : 's'} overdue, within the ${input.gracePeriodDays}-day grace window.`,
    };
  }
  const daysOverdue = daysBetween(due, input.now);
  return {
    id: 'timing', label, status: 'fail',
    reason: `Not yet submitted. ${daysOverdue} days overdue, past the ${input.gracePeriodDays}-day grace window.`,
  };
}

function checkCapturedInPeriod(input: TeamHealthInput): CheckResult {
  const label = 'Evidence captured in period';
  if (input.attachments.length === 0) {
    return { id: 'capturedInPeriod', label, status: 'pending', reason: 'No attachments to check yet.' };
  }

  // periodStart is inclusive-from-midnight as-is (a captured timestamp "on" that calendar
  // day already satisfies >= midnight). periodEnd needs the same exclusive-cutoff treatment
  // checkTiming uses - a capture at 2pm on the last grace day is still inside the period,
  // not just one parsed as that day's midnight.
  const periodStart = parseDate(input.reviewPeriodStart);
  const periodEndCutoff = addDays(parseDate(input.reviewPeriodEnd), 1 + input.gracePeriodDays);
  const submittedAt = input.submittedAt ? parseDate(input.submittedAt) : null;
  const periodLabel = `${input.reviewPeriodStart} to ${input.reviewPeriodEnd}`;

  for (const a of input.attachments) {
    const captured = parseDate(a.capturedAt);
    if (captured.getTime() < periodStart.getTime() || captured.getTime() >= periodEndCutoff.getTime()) {
      return {
        id: 'capturedInPeriod', label, status: 'fail',
        reason: `Captured ${formatShortDate(a.capturedAt)}, outside ${periodLabel}.`,
      };
    }
    if (submittedAt && captured.getTime() > submittedAt.getTime()) {
      return {
        id: 'capturedInPeriod', label, status: 'fail',
        reason: `Captured ${formatShortDate(a.capturedAt)}, after the submission itself.`,
      };
    }
  }
  return { id: 'capturedInPeriod', label, status: 'pass', reason: `All attachments captured within ${periodLabel}.` };
}

function checkRequirementsComplete(input: TeamHealthInput): CheckResult {
  const label = 'Requirements complete';
  const total = input.requirements.length;
  if (total === 0) {
    return { id: 'requirementsComplete', label, status: 'pass', reason: 'No requirements apply to this team.' };
  }
  const doneCount = input.requirements.filter((r) => r.done).length;
  if (doneCount === total) {
    return { id: 'requirementsComplete', label, status: 'pass', reason: `All ${total} requirement${total === 1 ? '' : 's'} complete.` };
  }
  if (doneCount === 0) {
    return { id: 'requirementsComplete', label, status: 'fail', reason: `0 of ${total} requirements complete.` };
  }
  return { id: 'requirementsComplete', label, status: 'warning', reason: `${doneCount} of ${total} requirements complete.` };
}

function checkAttachmentProvided(input: TeamHealthInput): CheckResult {
  const label = 'Attachment provided';
  if (input.attachments.length === 0) {
    return { id: 'attachmentProvided', label, status: 'fail', reason: 'No attachments provided.' };
  }
  const n = input.attachments.length;
  return { id: 'attachmentProvided', label, status: 'pass', reason: `${n} attachment${n === 1 ? '' : 's'} provided.` };
}

const STATUS_RANK: Record<CheckStatus, number> = { fail: 3, pending: 2, warning: 1, pass: 0 };

function worstOf(checks: CheckResult[]): TeamHealthStatus {
  let worst: CheckStatus = 'pass';
  for (const c of checks) {
    if (STATUS_RANK[c.status] > STATUS_RANK[worst]) worst = c.status;
  }
  switch (worst) {
    case 'fail': return 'Failing';
    case 'pending': return 'Pending';
    case 'warning': return 'Needs attention';
    default: return 'Healthy';
  }
}

export function computeTeamHealth(input: TeamHealthInput): TeamHealthResult {
  const checks = [
    checkTiming(input),
    checkCapturedInPeriod(input),
    checkRequirementsComplete(input),
    checkAttachmentProvided(input),
  ];
  return { status: worstOf(checks), checks };
}

/** Overall evidence status across every team - same worst-of logic, one level up. The
 * evidence record is only Healthy when every team is Healthy, per spec. */
export function computeOverallHealth(teamStatuses: TeamHealthStatus[]): TeamHealthStatus {
  if (teamStatuses.length === 0) return 'Pending';
  const rank: Record<TeamHealthStatus, number> = { Failing: 3, Pending: 2, 'Needs attention': 1, Healthy: 0 };
  let worst: TeamHealthStatus = 'Healthy';
  for (const s of teamStatuses) {
    if (rank[s] > rank[worst]) worst = s;
  }
  return worst;
}
